/**
 * This interpreter implements an explicit-control evaluator.
 *
 * Heavily adapted from https://github.com/source-academy/JSpike/
 */

/* tslint:disable:max-classes-per-file */

import { ErrorType } from "@sourceacademy/conductor/common";
import { ExprNS, StmtNS } from "../../ast-types";
import * as error from "../../errors/errors";
import { ConditionNotBoolError, UnsupportedOperandTypeError } from "../../errors/errors";
import { Group } from "../../stdlib/utils";
import { Token, TokenType } from "../../tokenizer";
import { CSEBreak, RecursivePartial, Result } from "../../types";
import { Closure, isStatementSequence } from "./closure";
import { Context } from "./context";
import { Control, ControlItem } from "./control";
import {
  createEnvironment,
  createProgramEnvironment,
  currentEnvironment,
  getGlobalEnvironment,
  popEnvironment,
  pushEnvironment,
} from "./environment";
import { handleRuntimeError, UnknownEvaluatorError } from "./error";
import * as instrCreator from "./instrCreator";
import { loadModules, moduleToPython, RelativeImportNotSupportedError } from "./modules";
import { evaluateBinaryExpression, evaluateUnaryExpression, isFalsy } from "./operators";
import { Stash, Value } from "./stash";
import { displayError } from "./streams";
import {
  AppInstr,
  AssmtInstr,
  BinOpInstr,
  BoolOpInstr,
  BranchInstr,
  BreakInstr,
  ContinueInstr,
  EndOfFunctionBodyInstr,
  EnvInstr,
  ForInstr,
  Instr,
  InstrType,
  ListAccessInstr,
  ListAssmtInstr,
  ListInstr,
  ModuleFunctionCallInstr,
  PopInstr,
  UnOpInstr,
  WhileInstr,
} from "./types";
import {
  checkStackOverFlow,
  envChanging,
  evaluateForIterator,
  evaluateListAssignment,
  generateForIncrement,
  getProgramEnvironment,
  isInstr,
  isNode,
  pyDefineVariable,
  pyGetGlobalVariable,
  resolvesToGlobal,
  pyGetNonlocalVariable,
  pyGetVariable,
  pySetNonlocalVariable,
  scanForAssignments,
  scanForGlobalDeclarations,
  scanForNonlocalDeclarations,
} from "./utils";

export interface IOptions {
  isPrelude: boolean;
  groups: Group[];
  stepLimit: number;
  variant: number;
  recursionLimit: number;
}

type CmdEvaluator<T extends ControlItem> = (
  code: string,
  command: T,
  context: Context,
  control: Control,
  stash: Stash,
  isPrelude: boolean,
  variant: number,
) => void | Promise<void>;

/**
 * Function that returns the appropriate Promise<Result> given the output of CSE machine evaluating, depending
 * on whether the program is finished evaluating, ran into a breakpoint or ran into an error.
 * @param context The context of the program.
 * @param value The value of CSE machine evaluating the program.
 * @returns The corresponding promise.
 */
export function CSEResultPromise(context: Context, value: Value): Promise<Result> {
  return new Promise((resolve, _reject) => {
    if (value instanceof CSEBreak) {
      resolve({ status: "suspended-cse-eval", context });
    } else if (value.type === "error") {
      resolve({ status: "finished", context, value });
    } else {
      resolve({ status: "finished", context, value });
    }
  });
}

let source = "";

/**
 * Function to be called when a program is to be interpreted using
 * the explicit control evaluator.
 *
 * @param program The program to evaluate.
 * @param context The context to evaluate the program in.
 * @param options Evaluation options.
 * @returns The result of running the CSE machine.
 */
export async function evaluate(
  code: string,
  program: StmtNS.Stmt,
  context: Context,
  options: RecursivePartial<IOptions> = {},
): Promise<Value> {
  const opts: IOptions = {
    isPrelude: false,
    groups: [],
    stepLimit: -1,
    recursionLimit: 1024,
    variant: 4,
    ...(options as Partial<IOptions>),
  };

  source = code;

  try {
    // TODO: is undefined variables check necessary for Python?
    // checkProgramForUndefinedVariables(program, context)
  } catch (error) {
    return displayError(context, error, ErrorType.EVALUATOR_RUNTIME);
  }

  try {
    context.runtime.isRunning = true;
    context.control = new Control(program);
    context.stash = new Stash();

    await evaluateImports(program as StmtNS.FileInput, context, code);

    // Adaptation for new feature
    const result = await runCSEMachine(
      code,
      context,
      context.control,
      context.stash,
      opts.stepLimit,
      opts.recursionLimit,
      opts.variant,
      opts.isPrelude,
    );
    return result;
  } catch (error) {
    return await displayError(context, error, ErrorType.EVALUATOR_RUNTIME);
  } finally {
    context.runtime.isRunning = false;
  }
}

type ModuleImportMapValue = { name: string; alias: string | undefined; node: StmtNS.FromImport };
function filterImportDeclarations(program: StmtNS.FileInput): Map<string, ModuleImportMapValue[]> {
  const importNodeMap = new Map<string, ModuleImportMapValue[]>();
  for (const stmt of program.statements) {
    if (stmt instanceof StmtNS.FromImport) {
      const moduleName = stmt.module.lexeme;
      if (!importNodeMap.has(moduleName)) {
        importNodeMap.set(moduleName, []);
      }
      importNodeMap.get(moduleName)!.push(
        ...stmt.names.map(spec => ({
          name: spec.name.lexeme,
          alias: spec.alias?.lexeme,
          node: stmt,
        })),
      );
    }
  }
  return importNodeMap;
}

async function evaluateImports(
  program: StmtNS.FileInput,
  context: Context,
  code: string,
): Promise<void> {
  const importNodeMap = filterImportDeclarations(program);
  if (importNodeMap.size === 0) {
    return;
  }
  if (context.evaluator === null || context.conductor === null) {
    throw new Error("Context is not properly initialized with evaluator and conductor");
  }

  for (const nodes of importNodeMap.values()) {
    const offending = nodes.find(n => n.node.level > 0);
    if (offending !== undefined) {
      handleRuntimeError(context, new RelativeImportNotSupportedError(offending.node));
    }
  }

  await loadModules(context, [...importNodeMap.keys()]);
  for (const [moduleName, nodes] of importNodeMap) {
    for (const node of nodes) {
      const importedFunc = context.nativeStorage.loadedModules[moduleName]?.[node.name];
      if (!importedFunc) {
        throw new error.ModuleFunctionNotFoundError(moduleName, moduleName, node.name, node.node);
      }
    }
    await Promise.all(
      nodes.map(async node => {
        const importedFunc = context.nativeStorage.loadedModules[moduleName][node.name];
        pyDefineVariable(
          context,
          node.alias || node.name,
          await moduleToPython(context, code, undefined, importedFunc.value),
        );
      }),
    );
  }
}

/**
 * The primary runner/loop of the explicit control evaluator.
 *
 * @param context The context to evaluate the program in.
 * @param control Points to the current Control stack.
 * @param stash Points to the current Stash.
 * @param stepLimit Maximum number of steps to execute.
 * @param recursionLimit Maximum depth of recursion allowed.
 * @param variant The language variant being executed.
 * @param isPrelude Whether the program is the prelude.
 * @returns The top value of the stash after execution.
 */
export async function runCSEMachine(
  code: string,
  context: Context,
  control: Control,
  stash: Stash,
  stepLimit: number,
  recursionLimit: number,
  variant: number,
  isPrelude: boolean = false,
): Promise<Value> {
  const eceState = generateCSEMachineStateStream(
    code,
    context,
    control,
    stash,
    stepLimit,
    recursionLimit,
    variant,
    isPrelude,
  );

  // Execute the generator until it completes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for await (const _value of eceState) {
  }

  // Return the value at the top of the storage as the result
  const result = stash.peek();
  return result !== undefined ? result : { type: "none" };
}

/**
 * Generator function that yields the state of the CSE Machine at each step.
 *
 * @param context The context of the program.
 * @param control The control stack.
 * @param stash The stash storage.
 * @param stepLimit Maximum number of steps to execute.
 * @param recursionLimit Maximum depth of recursion allowed.
 * @param variant The language variant being executed.
 * @param isPrelude Whether the program is the prelude.
 * @yields The current state of the stash, control stack, and step count.
 */
export async function* generateCSEMachineStateStream(
  code: string,
  context: Context,
  control: Control,
  stash: Stash,
  stepLimit: number,
  recursionLimit: number,
  variant: number,
  isPrelude: boolean = false,
) {
  // Stamped here, not just by evaluate(), since this is the one function every
  // execution path funnels through (evaluate()'s own runCSEMachine, and
  // PyCseMachinePlugin.ts's collectSnapshots, both call this directly) — see
  // Context.variant's own doc comment for why error construction needs it.
  context.variant = variant;

  // Evaluate imports before starting the main evaluation loop
  await evaluateImports(control.peek() as StmtNS.FileInput, context, code);

  // steps: number of steps completed
  let steps = 0;

  let command = control.peek();

  // Push first node to be evaluated into context.
  // The typeguard is there to guarantee that we are pushing a node (which should always be the case)
  if (command && isNode(command)) {
    context.runtime.nodes.unshift(command);
  }

  // Define the program
  const globalEnvironment = getGlobalEnvironment(context);
  if (globalEnvironment) {
    pyDefineVariable(context, "__program__", { type: "string", value: code }, globalEnvironment);
  }
  while (command) {
    // Step limit reached, stop further evaluation
    if (!isPrelude && steps === stepLimit) {
      const node = isNode(command) ? command : command.srcNode;
      const stmtOrExpr = isStatementSequence(node) ? node.body[0] : node;
      handleRuntimeError(context, new error.StepLimitExceededError(source, stmtOrExpr));
    }

    if (!isPrelude && envChanging(command)) {
      // command is evaluated on the next step
      // Hence, next step will change the environment
      context.runtime.changepointSteps.push(steps + 1);
    }

    // A call resolving to the `breakpoint` builtin, detected by identity (not source text) so
    // aliasing (e.g. `bp = breakpoint; bp()`) is caught too — mirrors the stepper's breakpoint
    // detection in reduce.ts. Real Python's `breakpoint(*args, **kws)` takes any number of
    // arguments (forwarded to sys.breakpointhook, which this builtin already ignores either way —
    // see stdlib/misc.ts), so this fires for any arity, not just zero. APPLICATION pops its callee
    // off the stash itself, after popping `numOfArgs` args first — by the time this instruction is
    // about to run, all `numOfArgs` args are already evaluated and sit on top of the callee, so
    // peekAt(numOfArgs) reaches down past them to the callee, still before APPLICATION runs.
    if (!isPrelude && isInstr(command) && command.instrType === InstrType.APPLICATION) {
      const callee = stash.peekAt(command.numOfArgs);
      if (callee?.type === "builtin" && callee.name === "breakpoint") {
        // `steps` here is the count as of the end of the *previous* iteration — i.e. the
        // stepIndex (collectSnapshots stores `steps - 1` per yield) of the snapshot that has
        // this very APPLICATION instruction on top of its control, which is what "steps - 1"
        // below reproduces.
        context.runtime.breakpointSteps.push(steps - 1);
        context.runtime.break = true;
      }
    }

    // A node flagged by `markBreakpoints` (a gutter click resolved to its closest enclosing
    // statement — see `breakpoints.ts`) is treated exactly like an explicit `breakpoint()` call:
    // recorded the moment it's about to be evaluated, using the same `steps - 1` step-index
    // convention as the block above.
    if (!isPrelude && isNode(command) && command.hasBreakpoint) {
      context.runtime.breakpointSteps.push(steps - 1);
      context.runtime.break = true;
    }

    control.pop();

    if (isNode(command)) {
      const node = command;

      context.runtime.nodes.shift();
      context.runtime.nodes.unshift(command);
      const nodeKind = node.kind;
      if (!isDeclaredEvaluator(nodeKind)) {
        handleRuntimeError(context, new UnknownEvaluatorError(node.kind));
      }
      await cmdEvaluators[nodeKind](
        code,
        node as never,
        context,
        control,
        stash,
        isPrelude,
        variant,
      );

      if (context.runtime.break && context.runtime.debuggerOn) {
        // TODO
        // We can put this under isNode since context.runtime.break
        // will only be updated after a debugger statement and so we will
        // run into a node immediately after.
        // With the new evaluator, we don't return a break
        // return new CSEBreak()
      }
    } else {
      // Command is an instruction
      const instrType = command.instrType;
      if (!isDeclaredEvaluator(instrType)) {
        handleRuntimeError(context, new UnknownEvaluatorError(command.instrType));
      }
      await cmdEvaluators[instrType](
        code,
        command as never,
        context,
        control,
        stash,
        isPrelude,
        variant,
      );
      checkStackOverFlow(code, command, context, control, recursionLimit);
    }

    command = control.peek();

    steps += 1;
    if (!isPrelude) {
      context.runtime.envStepsTotal = steps;
    }

    yield { stash, control, steps };
  }
}

function isDeclaredEvaluator(kind: string): kind is keyof CmdEvaluators {
  return kind in cmdEvaluators;
}
type ExprKeys = Exclude<
  keyof typeof ExprNS,
  | "Expr"
  | "MultiLambda"
  | "Starred"
  | "Tuple"
  | "Dict"
  | "Keyword"
  | "Attribute"
  | "CompareChain"
  | "Set"
  | "Comprehension"
  | "Yield"
  | "Slice"
  | "DoubleStarred"
>;
type StmtKeys = Exclude<
  keyof typeof StmtNS,
  | "Stmt"
  | "AnnAssign"
  | "Assert"
  | "TupleAssign"
  | "Class"
  | "Decorated"
  | "Try"
  | "Raise"
  | "With"
  | "Del"
  | "Import"
  | "AugAssign"
  | "AttributeAssign"
  | "ForUnpack"
>;
type InstrKeys = Exclude<InstrType, "Assert" | "NonLocal" | "Import" | "Program">;
type CmdEvaluators = {
  [K in ExprKeys]: CmdEvaluator<InstanceType<(typeof ExprNS)[K]>>;
} & {
  [K in StmtKeys]: CmdEvaluator<InstanceType<(typeof StmtNS)[K]>>;
} & {
  [K in InstrKeys]: CmdEvaluator<Extract<Instr, { instrType: K }>>;
};
const cmdEvaluators: CmdEvaluators = {
  /**
   * AST Nodes
   */

  FileInput: function (
    _code: string,
    fileInput: StmtNS.FileInput,
    context: Context,
    control: Control,
    _stash: Stash,
    isPrelude: boolean,
  ) {
    // Clean up non-global, non-program, and non-preparation environments
    while (
      currentEnvironment(context).name !== "global" &&
      currentEnvironment(context).name !== "programEnvironment" &&
      currentEnvironment(context).name !== "prelude"
    ) {
      popEnvironment(context);
    }

    // Create a new program environment if needed.
    if (
      fileInput.statements.length > 0 &&
      currentEnvironment(context).name !== "programEnvironment"
    ) {
      const programEnv = createProgramEnvironment(context, isPrelude);
      pushEnvironment(context, programEnv);
    }

    // Push the block body as a sequence of statements onto the control stack
    const seq = fileInput.statements.slice().reverse();
    control.push(...seq);
  },

  SimpleExpr: function (
    _code: string,
    simpleExpr: StmtNS.SimpleExpr,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    // Discard the evaluated expression off the stack,
    // since Python statements are not value producing
    control.push(instrCreator.popInstr(simpleExpr));
    control.push(simpleExpr.expression);
  },

  Literal: function (
    _code: string,
    literal: ExprNS.Literal,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    if (typeof literal.value === "number") {
      stash.push({ type: "number", value: literal.value });
    } else if (typeof literal.value === "boolean") {
      stash.push({ type: "bool", value: literal.value });
    } else if (typeof literal.value === "string") {
      stash.push({ type: "string", value: literal.value });
    } else {
      stash.push({ type: "none" });
    }
  },

  BigIntLiteral: function (
    _code: string,
    bigIntLiteral: ExprNS.BigIntLiteral,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    stash.push({ type: "bigint", value: BigInt(bigIntLiteral.value) });
  },

  Unary: function (
    _code: string,
    unary: ExprNS.Unary,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const op_instr = instrCreator.unOpInstr(unary.operator.type, unary);
    control.push(op_instr);
    control.push(unary.right);
  },

  Binary: function (
    _code: string,
    binary: ExprNS.Binary,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const op_instr = instrCreator.binOpInstr(binary.operator.type, binary);
    control.push(op_instr);
    control.push(binary.right);
    control.push(binary.left);
  },

  BoolOp: function (
    _code: string,
    boolOp: ExprNS.BoolOp,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instrCreator.boolOpInstr(boolOp.operator.type, boolOp));
    control.push(boolOp.left);
  },

  Grouping: function (
    _code: string,
    grouping: ExprNS.Grouping,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(grouping.expression);
  },

  Complex: function (
    _code: string,
    complex: ExprNS.Complex,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    stash.push({ type: "complex", value: complex.value });
  },

  None: function (
    _code: string,
    _command: ExprNS.None,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    stash.push({ type: "none" });
  },

  Global: function () {
    // `global x` is a declaration — no runtime effect; the interpreter uses
    // closure.globalVariables (populated at FunctionDef time) to route reads
    // and writes to the module-level environment.
  },

  NonLocal: function () {
    // `nonlocal x` is a declaration — no runtime effect; the interpreter uses
    // closure.nonlocalVariables (populated at FunctionDef time) to route reads
    // and writes to the nearest enclosing function environment.
  },

  Variable: function (
    code: string,
    variable: ExprNS.Variable,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const name = variable.name.lexeme;
    const currentEnv = currentEnvironment(context);
    const isGlobal = resolvesToGlobal(currentEnv, name);
    const isNonlocal = currentEnv.closure?.nonlocalVariables.has(name) ?? false;
    const value = isGlobal
      ? pyGetGlobalVariable(code, context, name, variable)
      : isNonlocal
        ? pyGetNonlocalVariable(code, context, name, variable)
        : pyGetVariable(code, context, name, variable);
    stash.push(value);
  },

  Compare: function (
    _code: string,
    compareNode: ExprNS.Compare,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const op_instr = instrCreator.binOpInstr(compareNode.operator.type, compareNode);

    control.push(op_instr);
    control.push(compareNode.right);
    control.push(compareNode.left);
  },

  Assign: function (
    _code: string,
    assignNode: StmtNS.Assign,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    if (assignNode.target instanceof ExprNS.Subscript) {
      // As in Python, the right-hand side is evaluated first, then the list, then the index
      // (the control is a stack: the last item pushed runs first).
      control.push(instrCreator.listAssmtInstr(assignNode.target));
      control.push(assignNode.target.index);
      control.push(assignNode.target.value);
      control.push(assignNode.value);
      return;
    }

    const assmtInstr = instrCreator.assmtInstr(
      assignNode.target.name.lexeme,
      false,
      true,
      assignNode,
    );

    control.push(assmtInstr);
    control.push(assignNode.value);
  },

  Call: function (
    _code: string,
    callNode: ExprNS.Call,
    context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const spreadIndices = callNode.args.reduce<number[]>((acc, arg, i) => {
      if (arg instanceof ExprNS.Starred) acc.push(i);
      return acc;
    }, []);

    control.push(instrCreator.appInstr(callNode.args.length, callNode, spreadIndices));
    for (let i = callNode.args.length - 1; i >= 0; i--) {
      const arg = callNode.args[i];
      // Push the inner expression for Starred nodes — the spread flattening
      // happens at application time using spreadIndices.
      control.push(arg instanceof ExprNS.Starred ? arg.value : arg);
    }
    control.push(callNode.callee);
  },

  FunctionDef: function (
    _code: string,
    functionDefNode: StmtNS.FunctionDef,
    context: Context,
    _control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const globalVariables = scanForGlobalDeclarations(functionDefNode.body);
    const nonlocalVariables = scanForNonlocalDeclarations(functionDefNode.body);
    const localVariables = scanForAssignments(
      functionDefNode.body,
      globalVariables,
      nonlocalVariables,
    );
    // Parameters are local to the function too — a nested function's `nonlocal x` must be
    // able to target an enclosing function's parameter, not just its assigned/for-target
    // locals (a parameter is never global/nonlocal itself; the resolver already rejects
    // that combination as a scope conflict, so no need to filter here).
    for (const param of functionDefNode.parameters) {
      localVariables.add(param.lexeme);
    }
    const closure = Closure.makeFromFunctionDef(
      functionDefNode,
      currentEnvironment(context),
      context,
      localVariables,
      globalVariables,
      nonlocalVariables,
    );
    pyDefineVariable(context, functionDefNode.name.lexeme, { type: "closure", closure });
  },

  Lambda: function (
    _code: string,
    lambdaNode: ExprNS.Lambda,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const localVariables = scanForAssignments(lambdaNode.body);
    for (const param of lambdaNode.parameters) {
      localVariables.add(param.lexeme);
    }
    const closure = Closure.makeFromLambda(
      lambdaNode,
      currentEnvironment(context),
      context,
      localVariables,
    );
    stash.push({ type: "closure", closure });
  },

  Return: function (
    _code: string,
    returnNode: StmtNS.Return,
    _context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    let head;
    while (true) {
      head = control.pop();
      if (!head || (isInstr(head) && head.instrType === InstrType.ENVIRONMENT)) {
        break;
      }
    }
    if (head) {
      control.push(head);
    }
    if (returnNode.value) {
      control.push(returnNode.value);
    } else {
      // implicit None return
      stash.push({ type: "none" });
    }
  },

  For: function (
    code: string,
    command: StmtNS.For,
    context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const iterator = evaluateForIterator(code, context, command);

    control.push(instrCreator.forInstr(command, command.body));
    control.push(iterator.step);
    control.push(iterator.end);
    control.push(iterator.start);
  },

  While: function (
    _code: string,
    whileNode: StmtNS.While,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const instr = instrCreator.whileInstr(whileNode, whileNode.condition, {
      kind: "StatementSequence",
      body: whileNode.body,
    });
    control.push(instr);
    control.push(whileNode.condition);
  },

  Break: function (
    _code: string,
    command: StmtNS.Break,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instrCreator.breakInstr(command));
  },

  Continue: function (
    _code: string,
    command: StmtNS.Continue,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instrCreator.continueInstr(command));
  },

  If: function (
    _code: string,
    ifNode: StmtNS.If,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const branch = instrCreator.branchInstr(
      { kind: "StatementSequence", body: ifNode.body },
      ifNode.elseBlock
        ? Array.isArray(ifNode.elseBlock)
          ? // 'else' block
            { kind: "StatementSequence", body: ifNode.elseBlock }
          : // 'elif' block
            ifNode.elseBlock
        : // 'else' block dont exist
          null,
      ifNode,
    );
    control.push(branch);
    control.push(ifNode.condition);
  },

  List: function (
    _code: string,
    command: ExprNS.List,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instrCreator.listInstr(command.elements.length, command));

    for (let i = command.elements.length - 1; i >= 0; i--) {
      control.push(command.elements[i]);
    }
  },

  Subscript: function (
    _code: string,
    subscriptNode: ExprNS.Subscript,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instrCreator.listAccessInstr(subscriptNode));
    control.push(subscriptNode.index);
    control.push(subscriptNode.value);
  },

  Ternary: function (
    _code: string,
    ternaryNode: ExprNS.Ternary,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const branch = instrCreator.branchInstr(
      ternaryNode.consequent,
      ternaryNode.alternative,
      ternaryNode,
    );
    control.push(branch);
    control.push(ternaryNode.predicate);
  },

  FromImport: function () {
    // TODO: nothing to do for now, we can implement it for CSE instructions later on
    // All modules are preloaded into the global environment by the runner.
    // When the code later uses the module name (e.g., 'runes'), pyGetVariable
    // will find it in the global scope.
  },

  Pass: function () {
    // No action needed for 'pass' statement
  },

  /**
   * Instructions
   */

  [InstrType.ASSIGNMENT]: function (
    code: string,
    instr: AssmtInstr,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const value = stash.pop();

    if (value) {
      const currentEnv = currentEnvironment(context);
      const isGlobal = currentEnv.closure?.globalVariables.has(instr.symbol) ?? false;
      const isNonlocal = currentEnv.closure?.nonlocalVariables.has(instr.symbol) ?? false;
      if (isGlobal) {
        const progEnv = getProgramEnvironment(context) ?? currentEnv;
        pyDefineVariable(context, instr.symbol, value, progEnv);
      } else if (isNonlocal) {
        pySetNonlocalVariable(code, context, instr.symbol, value, instr.srcNode as ExprNS.Expr);
      } else {
        pyDefineVariable(context, instr.symbol, value);
      }
    }
  },

  [InstrType.LIST_ASSIGNMENT]: function (
    code: string,
    instr: ListAssmtInstr,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    // The stash holds the value, the list and the index, in the order they were evaluated.
    const index = stash.pop();
    const list = stash.pop();
    const value = stash.pop();

    evaluateListAssignment(code, instr.srcNode as StmtNS.Assign, context, list, index, value);
  },

  [InstrType.BREAK]: function (
    _code: string,
    command: BreakInstr,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const top = control.peek();
    if (!top) {
      return;
    }
    if (isNode(top) || (top.instrType !== InstrType.WHILE && top.instrType !== InstrType.FOR)) {
      control.pop();
      control.push(command);
      return;
    }
    control.pop();
  },

  [InstrType.CONTINUE]: function (
    _code: string,
    command: ContinueInstr,
    _context: Context,
    control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    const top = control.pop();
    if (!top) return;
    if (isNode(top) || top.instrType !== InstrType.CONTINUE_MARKER) {
      control.push(command);
    }
  },

  [InstrType.UNARY_OP]: function (
    code: string,
    instr: UnOpInstr,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const argument = stash.pop();
    if (argument) {
      const result = evaluateUnaryExpression(
        code,
        instr.srcNode as ExprNS.Unary,
        context,
        instr.symbol,
        argument,
      );
      stash.push(result);
    }
  },

  [InstrType.BINARY_OP]: function (
    code: string,
    instr: BinOpInstr,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
    variant: number,
  ) {
    const right = stash.pop();
    const left = stash.pop();
    if (left && right && variant) {
      const result = evaluateBinaryExpression(
        code,
        instr.srcNode as ExprNS.Binary,
        context,
        instr.symbol,
        left,
        right,
        variant,
      );
      stash.push(result);
    }
  },

  [InstrType.BOOL_OP]: function (
    code: string,
    instr: BoolOpInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const left = stash.pop();
    const boolOpNode = instr.srcNode as ExprNS.BoolOp;
    const right = boolOpNode.right;
    if (left) {
      if (left.type !== "bool") {
        handleRuntimeError(
          context,
          new UnsupportedOperandTypeError(
            code,
            boolOpNode,
            context,
            left.type,
            "",
            boolOpNode.operator.type,
          ),
        );
      }
      const falsy = isFalsy(left);
      const operator = instr.symbol;
      if (operator == TokenType.AND && falsy) {
        stash.push(left);
      } else if (operator == TokenType.AND && !falsy) {
        control.push(right);
      } else if (operator == TokenType.OR && falsy) {
        control.push(right);
      } else {
        stash.push(left);
      }
    }
  },

  [InstrType.POP]: function (
    _code: string,
    _command: PopInstr,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    stash.pop();
  },

  [InstrType.LIST]: function (
    _code: string,
    instr: ListInstr,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const elements: Value[] = [];
    for (let i = 0; i < instr.numOfElements; i++) {
      const element = stash.pop();
      if (element) {
        elements.unshift(element);
      }
    }
    const listValue: Value = { type: "list", value: elements };
    stash.push(listValue);
  },

  [InstrType.WHILE]: function (
    code: string,
    instr: WhileInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const condition = stash.pop();
    if (!condition) return;
    if (condition.type !== "bool") {
      handleRuntimeError(
        context,
        new error.TypeError(code, instr.srcNode as StmtNS.Stmt, context, condition.type),
      );
      return;
    }
    if (!isFalsy(condition)) {
      control.push(instr);
      control.push(instr.test);
      control.push(instrCreator.continueMarkerInstr(instr.srcNode));
      control.push(...instr.body.body.slice().reverse());
    }
  },
  [InstrType.FOR]: function (
    code: string,
    instr: ForInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const step = stash.pop();
    const end = stash.pop();
    const start = stash.pop();
    if (start && end && step) {
      const node = instr.srcNode as StmtNS.For;
      if (start.type !== "bigint" || end.type !== "bigint" || step.type !== "bigint") {
        handleRuntimeError(
          context,
          new error.TypeError(
            code,
            node.iter,
            context,
            [start.type, end.type, step.type].filter(t => t !== "bigint")[0],
          ),
        );
      }
      if (step.value === 0n) {
        handleRuntimeError(
          context,
          new error.UserError("ValueError: range() arg 3 must not be zero", node.iter),
        );
        return;
      }
      if (
        (step.value < 0 && end.value >= start.value) ||
        (step.value > 0 && end.value <= start.value)
      ) {
        return;
      }
      control.push(instr);
      // Line is the real for-statement's line — this per-iteration bookkeeping is
      // logically part of evaluating that statement, not a separate, lineless step.
      const forLine = node.startToken.line;
      const generateBigIntLiteral = (value: bigint): ExprNS.BigIntLiteral => {
        const token = new Token(TokenType.BIGINT, value.toString(), forLine, 0, -1);
        token.synthetic = true;
        return new ExprNS.BigIntLiteral(token, token, value.toString());
      };
      const v1Lit = generateBigIntLiteral(start.value);
      const v3Lit = generateBigIntLiteral(step.value);
      const plusToken = new Token(TokenType.PLUS, "+", forLine, 0, -1);
      plusToken.synthetic = true;
      const nextStartExpr = new ExprNS.Binary(plusToken, plusToken, v1Lit, plusToken, v3Lit);
      Object.assign(nextStartExpr, { syntheticLabel: `${start.value}+${step.value}` });
      control.push(generateBigIntLiteral(step.value));
      control.push(generateBigIntLiteral(end.value));
      control.push(nextStartExpr);
      control.push(instrCreator.continueMarkerInstr(node));
      control.push(...instr.body.slice().reverse());
      control.push(generateForIncrement(node.target.lexeme, start.value, forLine));
    }
  },
  [InstrType.CONTINUE_MARKER]: function () {},
  [InstrType.APPLICATION]: async function (
    code: string,
    instr: AppInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    // This is the point at which the call is actually applied.  Recording it
    // while merely scheduling its children made a nested argument call leak
    // into the outer call's module-interface error.
    context.evaluator?.setCurrentCall(instr.srcNode);
    // Tail-Call Optimisation
    const topElement = control.peek();
    let shouldPushEnvInstr = true;
    if (
      topElement !== undefined &&
      isInstr(topElement) &&
      topElement.instrType === InstrType.ENVIRONMENT
    ) {
      shouldPushEnvInstr = false;
      while (currentEnvironment(context).id !== topElement.env.id) {
        popEnvironment(context);
      }
    }

    const numOfArgs = instr.numOfArgs;

    const rawArgs: Value[] = [];
    for (let i = 0; i < numOfArgs; i++) {
      const arg = stash.pop();
      if (arg) {
        rawArgs.unshift(arg);
      }
    }

    // Flatten spread args: starred indices contain list values that
    // need to be expanded inline.
    const spreadSet = new Set(instr.spreadIndices);
    const args: Value[] =
      spreadSet.size === 0
        ? rawArgs
        : rawArgs.flatMap((val, i) => {
            if (!spreadSet.has(i)) return val;
            if (val?.type === "list") {
              return val.value;
            }
            handleRuntimeError(
              context,
              new error.TypeError(code, instr.srcNode, context, val ? val.type : "NoneType"),
            );
          });

    const callable = stash.pop();

    if (callable?.type == "closure") {
      const closure = callable.closure;
      if (shouldPushEnvInstr) {
        const callerEnv = currentEnvironment(context);
        control.push(instrCreator.envInstr(callerEnv, instr.srcNode));
      }
      if (closure.node.kind === "FunctionDef") {
        control.push(instrCreator.endOfFunctionBodyInstr(instr.srcNode));
      }

      const newEnv = createEnvironment(code, context, closure, args, instr.srcNode);
      pushEnvironment(context, newEnv);

      const closureNode = closure.node;
      if (closureNode.kind === "FunctionDef") {
        const bodyStmts = closureNode.body.slice().reverse();
        control.push(...bodyStmts);
      } else {
        const bodyExpr = closureNode.body;
        control.push(bodyExpr);
      }
    } else if (callable?.type === "builtin") {
      const result = await callable.func(args, code, instr.srcNode, context);
      if (result === undefined) {
        return;
      }
      if ("next" in result) {
        const funcCallInstr: ModuleFunctionCallInstr = {
          instrType: InstrType.MODULE_FUNCTION_CALL,
          generator: result,
          srcNode: instr.srcNode,
        };
        control.push(funcCallInstr);
      } else {
        stash.push(result);
      }
    } else {
      handleRuntimeError(
        context,
        new error.TypeError(
          code,
          instr.srcNode,
          context,
          callable ? callable.type : "NoneType",
          true,
        ),
      );
    }
  },

  [InstrType.LIST_ACCESS]: function (
    code: string,
    instr: ListAccessInstr,
    context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const index = stash.pop();
    const list = stash.pop();
    if (!list || (list.type !== "list" && list.type !== "string")) {
      handleRuntimeError(
        context,
        new error.TypeError(
          code,
          instr.srcNode as ExprNS.Expr,
          context,
          list ? list.type : "NoneType",
        ),
      );
    }
    if (!index || index.type !== "bigint") {
      handleRuntimeError(
        context,
        new error.ListIndexTypeError(code, instr.srcNode as ExprNS.Expr, context),
      );
    }
    const idx = Number(index.value);
    // TODO: make this O(1)
    const codePoints = list.type === "string" ? [...list.value] : undefined;
    const length = codePoints ? codePoints.length : list.value.length;
    if (idx < -length || idx >= length) {
      handleRuntimeError(
        context,
        new error.IndexError(code, instr.srcNode as ExprNS.Expr, idx, length, false),
      );
    }
    const wrappedIdx = idx < 0 ? idx + length : idx;
    if (list.type === "string") {
      stash.push({ type: "string", value: codePoints![wrappedIdx] });
    } else {
      stash.push(list.value[wrappedIdx]);
    }
  },

  // Used by both `if`/`elif` and the conditional expression -- one check-site covers both,
  // matching py2js's shared condBool / the stepper's shared contractConditional (py-slang#439).
  // Requires a genuine bool (docs/specs/python_typing_back.tex: "Following if and elif, Python
  // §x only allows boolean expressions") rather than isFalsy's general any-type truthiness,
  // mirroring BOOL_OP's identical check on and/or's left operand above (py-slang#436).
  [InstrType.BRANCH]: function (
    code: string,
    instr: BranchInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    const condition = stash.pop();

    if (condition && condition.type !== "bool") {
      const srcNode = instr.srcNode;
      const [conditionNode, contextLabel] =
        srcNode instanceof StmtNS.If
          ? [srcNode.condition, "if condition"]
          : [(srcNode as ExprNS.Ternary).predicate, "conditional expression condition"];
      handleRuntimeError(
        context,
        new ConditionNotBoolError(code, conditionNode, context, condition.type, contextLabel),
      );
    }

    if (condition && !isFalsy(condition)) {
      const consequent = instr.consequent;
      if (isStatementSequence(consequent)) {
        control.push(...consequent.body.slice().reverse());
      } else if (consequent) {
        control.push(consequent);
      }
    } else if (instr.alternate) {
      const alternate = instr.alternate;
      if (isStatementSequence(alternate)) {
        control.push(...alternate.body.slice().reverse());
      } else if (alternate) {
        control.push(alternate);
      }
    }
  },

  [InstrType.ENVIRONMENT]: function (
    _code: string,
    command: EnvInstr,
    context: Context,
    _control: Control,
    _stash: Stash,
    _isPrelude: boolean,
  ) {
    while (currentEnvironment(context).id !== command.env.id) {
      popEnvironment(context);
    }
  },

  [InstrType.END_OF_FUNCTION_BODY]: function (
    _code: string,
    _command: EndOfFunctionBodyInstr,
    _context: Context,
    _control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    stash.push({ type: "none" });
  },

  [InstrType.MODULE_FUNCTION_CALL]: async function (
    code: string,
    instr: ModuleFunctionCallInstr,
    context: Context,
    control: Control,
    stash: Stash,
    _isPrelude: boolean,
  ) {
    control.push(instr);
    const nextValue = await instr.generator.next();
    if (nextValue.done) {
      control.pop();
      stash.push(await moduleToPython(context, code, instr.srcNode, nextValue.value));
    }
  },
};
